import { Component, OnInit } from '@angular/core';
import { Manufacturer } from '../../models/catalog';
import { ManufacturerService } from '../../../services/manufacturer.service';

/**
 * The global manufacturer list. Catalog uploads name a manufacturer per row; this is where a brand
 * is added ahead of time or its spelling corrected.
 *
 * Renaming here renames it for every tenant and catalog that points at it — it is one row, not a
 * copy — so there is no delete: a brand in use cannot go, and one not in use costs nothing.
 *
 * Filtered in memory: the endpoint returns the whole (short) list and takes no search.
 */
@Component({
  selector: 'app-manufacturers',
  templateUrl: './manufacturers.component.html',
  styleUrls: ['./manufacturers.component.scss']
})
export class ManufacturersComponent implements OnInit {

  manufacturers: Manufacturer[] = [];
  loading = true;
  error: string | null = null;

  search = '';

  newName = '';
  adding = false;
  addError: string | null = null;

  editingId: number | null = null;
  editName = '';
  saving = false;
  editError: string | null = null;

  constructor(private manufacturerService: ManufacturerService) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.manufacturerService.getManufacturers().subscribe({
      next: (response) => {
        this.manufacturers = this.sorted(response.data ?? []);
        this.loading = false;
      },
      error: () => {
        this.error = 'Could not load manufacturers.';
        this.loading = false;
      }
    });
  }

  get filtered(): Manufacturer[] {
    const term = this.search.trim().toLowerCase();
    return term ? this.manufacturers.filter(m => m.name.toLowerCase().includes(term)) : this.manufacturers;
  }

  add(): void {
    const name = this.newName.trim();
    if (!name || this.adding) {
      return;
    }
    this.adding = true;
    this.addError = null;
    this.manufacturerService.create(name).subscribe({
      next: (created) => {
        // The backend hands back the existing row when the name is already taken, so replace rather
        // than append, or the list would show it twice.
        this.manufacturers = this.sorted([...this.manufacturers.filter(m => m.id !== created.id), created]);
        this.newName = '';
        this.adding = false;
      },
      error: (err) => {
        this.addError = this.messageOf(err, 'The manufacturer could not be added.');
        this.adding = false;
      }
    });
  }

  startEdit(manufacturer: Manufacturer): void {
    this.editingId = manufacturer.id;
    this.editName = manufacturer.name;
    this.editError = null;
  }

  cancelEdit(): void {
    this.editingId = null;
    this.editName = '';
    this.editError = null;
  }

  saveEdit(manufacturer: Manufacturer): void {
    const name = this.editName.trim();
    if (!name || this.saving) {
      return;
    }
    if (name === manufacturer.name) {
      this.cancelEdit();
      return;
    }
    this.saving = true;
    this.editError = null;
    this.manufacturerService.rename(manufacturer.id, name).subscribe({
      next: (renamed) => {
        this.manufacturers = this.sorted(this.manufacturers.map(m => m.id === renamed.id ? renamed : m));
        this.saving = false;
        this.cancelEdit();
      },
      error: (err) => {
        this.editError = this.messageOf(err, 'The name could not be saved.');
        this.saving = false;
      }
    });
  }

  trackById(_index: number, manufacturer: Manufacturer): number {
    return manufacturer.id;
  }

  private sorted(list: Manufacturer[]): Manufacturer[] {
    return [...list].sort((a, b) => a.name.localeCompare(b.name, 'sr'));
  }

  private messageOf(err: any, fallback: string): string {
    return err?.error?.exceptionMessage || err?.error?.message || fallback;
  }
}
